import { FormattedMessage } from 'react-intl';

import LinearProgressStyled from 'components/@extended/LinearProgress';
import InfoTooltip from 'components/InfoTooltip.tsx';
import { Progress, ProgressLabel } from 'components/ui/progress';
import MoneyDisplay from 'components/util/MoneyDisplay';
import { FinancialDataDto } from 'types/other';
import { useId } from 'react';
import { cn } from 'utils/cn';
import { toFixed } from 'utils/numberUtil';
import { GreyBoxes } from './GreyBox';

interface Props {
    /** `tile`: the new design's invoice case card, a read-only tile with the kit's progress bar. */
    variant: 'solid' | 'outlined' | 'tile';
    /** Classes for the outlined card, or for the solid variant's grey box. */
    className?: string;
    /** Classes for the outlined card's padded content. */
    contentClassName?: string;
    budget?: number;
    remaining?: number;
    used?: number;
    remainingInvoice?: number;
    currency?: string;
    capInput?: JSX.Element;
    financialData?: FinancialDataDto;
    isInvoice?: boolean;
}

/** The small print's size on the body's line height, which is what the rows are spaced by. */
const SMALL_TEXT = 'text-[12px] leading-[1.375rem]';

const signedAmountColor = (amount: number) => (amount < 0 ? 'text-error new:text-danger-ink' : 'text-fg');

const CapFeeBudgetBox = ({
    variant,
    className,
    contentClassName,
    budget,
    remaining,
    used,
    remainingInvoice,
    currency,
    capInput,
    financialData,
    isInvoice = false,
}: Props) => {
    const totalCapAmount = financialData?.totalCapAmount ?? budget ?? 0;
    const capUsageForWip = financialData?.capUsageForWip ?? used ?? 0;
    const remainingForWip = financialData?.remainingForWip ?? remaining ?? 0;
    const remainingInvoicedOnly = financialData?.remainingInvoicedOnly ?? remainingInvoice ?? 0;
    const isOutlined = variant === 'outlined';

    let budgetUsedPercentage: number;
    if (capUsageForWip === 0 && totalCapAmount === 0) {
        budgetUsedPercentage = 100;
    } else if (capUsageForWip !== 0 && totalCapAmount === 0) {
        budgetUsedPercentage = capUsageForWip;
    } else {
        budgetUsedPercentage = (capUsageForWip / totalCapAmount) * 100;
    }
    const budgetUsed = toFixed(budgetUsedPercentage, 2);
    const titleId = useId();
    if (variant === 'tile') {
        const caption = 'text-caption text-foreground-tertiary flex items-center gap-1';
        return (
            <div
                role="group"
                aria-labelledby={titleId}
                className={cn('bg-surface-tile flex flex-col gap-2.5 rounded-lg px-4 py-3', className)}
            >
                <div className="flex items-baseline justify-between gap-3">
                    <span id={titleId} className="text-meta text-foreground-secondary">
                        <FormattedMessage id="invoices.cap-fee-budget" />
                    </span>
                    <span className="text-body1 font-semibold tabular-nums">
                        <MoneyDisplay amount={totalCapAmount} currency={currency} />
                    </span>
                </div>
                <Progress value={Math.min(100, Number(budgetUsed))} className="gap-x-3 gap-y-1.5">
                    <ProgressLabel className="text-caption text-foreground-secondary font-normal">
                        <FormattedMessage id="invoices.cap-fee-budget-used" />
                    </ProgressLabel>
                    <span className="text-caption text-muted-foreground ms-auto tabular-nums">{budgetUsed}%</span>
                </Progress>
                <div className="text-caption flex items-center gap-1">
                    <span className={cn('tabular-nums', signedAmountColor(remainingForWip))}>
                        <FormattedMessage
                            id="invoices.remaining-with-value"
                            values={{
                                value: <MoneyDisplay key="value" amount={remainingForWip} currency={currency} />,
                            }}
                        />
                    </span>
                    <InfoTooltip
                        text={isInvoice ? 'invoices.cap-fee-budget-invoice-tooltip' : 'invoices.cap-fee-budget-tooltip'}
                    />
                </div>
                <div className="border-divider grid grid-cols-2 gap-x-6 border-t pt-2">
                    <div className="flex flex-col gap-0.5">
                        <span className={caption}>
                            <FormattedMessage id="invoices.remaining-vs-work-done" />
                            <InfoTooltip text="invoices.remaining-vs-work-done-tooltip" />
                        </span>
                        <span className={cn('text-body1 font-medium tabular-nums', signedAmountColor(remainingForWip))}>
                            <MoneyDisplay amount={remainingForWip} currency={currency} />
                        </span>
                    </div>
                    <div className="flex flex-col gap-0.5">
                        <span className={caption}>
                            <FormattedMessage id="invoices.remaining-for-invoicing" />
                            <InfoTooltip text="invoices.remaining-for-invoicing-tooltip" />
                        </span>
                        <span
                            className={cn(
                                'text-body1 font-medium tabular-nums',
                                signedAmountColor(remainingInvoicedOnly)
                            )}
                        >
                            <MoneyDisplay amount={remainingInvoicedOnly} currency={currency} />
                        </span>
                    </div>
                </div>
            </div>
        );
    }
    const content = (
        <>
            <div className={cn(!isOutlined && 'flex justify-between')}>
                <p className={isOutlined ? 'text-body1' : SMALL_TEXT}>
                    <FormattedMessage id="invoices.cap-fee-budget" />
                </p>
                <div className={cn('text-fg font-semibold', isOutlined && 'text-[1.5rem]')}>
                    <MoneyDisplay amount={totalCapAmount} currency={currency} />
                </div>
            </div>
            <div className={isOutlined ? 'mt-4' : 'mt-1'}>
                <div className="flex flex-row items-center gap-2">
                    <LinearProgressStyled variant="determinate" color="secondary" value={budgetUsed} />
                    <p className={cn(SMALL_TEXT, 'text-fg inline')}>{budgetUsed}%</p>
                </div>
                <div className="flex flex-row items-center gap-2">
                    <p className={cn(SMALL_TEXT, 'mt-1', signedAmountColor(remainingForWip))}>
                        <FormattedMessage
                            id="invoices.remaining-with-value"
                            values={{
                                value: <MoneyDisplay key="value" amount={remainingForWip} currency={currency} />,
                            }}
                        />
                    </p>
                    <InfoTooltip
                        text={isInvoice ? 'invoices.cap-fee-budget-invoice-tooltip' : 'invoices.cap-fee-budget-tooltip'}
                    />
                </div>
                <div className="mt-2">
                    <div className="flex flex-row gap-2">
                        <p className={cn(SMALL_TEXT, 'text-fg-secondary mb-1')}>
                            <FormattedMessage id="invoices.remaining-vs-work-done" />
                        </p>
                        <InfoTooltip text="invoices.remaining-vs-work-done-tooltip" />
                    </div>
                    <p className={cn(SMALL_TEXT, 'font-medium', signedAmountColor(remainingForWip))}>
                        <MoneyDisplay amount={remainingForWip} currency={currency} />
                    </p>
                </div>
                <div className="mt-2">
                    <div className="flex flex-row gap-2">
                        <p className={cn(SMALL_TEXT, 'text-fg-secondary mb-1')}>
                            <FormattedMessage id="invoices.remaining-for-invoicing" />
                        </p>
                        <InfoTooltip text="invoices.remaining-for-invoicing-tooltip" />
                    </div>
                    <p className={cn(SMALL_TEXT, 'font-medium', signedAmountColor(remainingInvoicedOnly))}>
                        <MoneyDisplay amount={remainingInvoicedOnly} currency={currency} />
                    </p>
                </div>
            </div>
            {capInput ? (
                <>
                    <hr className="border-divider my-2" />
                    {capInput}
                </>
            ) : null}
        </>
    );

    if (isOutlined) {
        return (
            <div className={cn('bg-paper text-fg border-divider overflow-hidden rounded-[4px] border', className)}>
                <div className={cn('p-5', contentClassName)}>{content}</div>
            </div>
        );
    }
    return <GreyBoxes className={className}>{content}</GreyBoxes>;
};

export default CapFeeBudgetBox;
